import {
  AppRoute,
  QueryReference,
  type PageAdminHomeData,
} from '@agentselly/immodossier-shared';
import { WrapperWidget } from '@agentselly/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useBackendQuery } from '~/helper/useBackendQuery';
import { useTranslate } from '@tolgee/react';
import { clsx } from 'clsx';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { AppLink } from '~/components/AppLink';
import type { ReactNode } from 'react';
import {
  V3ArrowIcon,
  V3DocIcon,
  V3EmptyBlock,
  V3UsersIcon,
} from '~/components/admin/V3EmptyBlock';
import { useAppParams } from '~/helper/useAppParams';

export const Route = createFileRoute(AppRoute.$LANG_$COMPANYID_ADMIN_HOME)({
  component: Body,
});

function Body() {
  const { t } = useTranslate();
  const { lang, companyId } = useAppParams();

  const query = useBackendQuery(QueryReference.PageAdminHome);

  if (query.isLoading) {
    return <LoadingWidget />;
  }

  if (query.isError) {
    return <ErrorWidget error={query.error} />;
  }

  const data = query.data || ({} as PageAdminHomeData);

  if (!data.v3Redesign) {
    return (
      <WrapperWidget>
        <p>{t('admin_home_content')}</p>
        <br />
        {Object.entries((query.data as Record<string, any>) || {}).map(
          (
            [
              key,
              value,
            ],
            index,
          ) => (
            <div
              key={index}
              className={clsx(
                'overflow-hidden py-2',
                index !== 0
                  ? 'border-gray-100 dark:border-gray-700 border-t-2'
                  : '',
              )}
            >
              <div>
                <span>{key}</span>
                <pre className="px-2 bg-primary-100 float-end rounded">
                  {JSON.stringify(value).replace(/"(.*?)"/, '$1')}
                </pre>
              </div>
            </div>
          ),
        )}
      </WrapperWidget>
    );
  }

  const head = (
    <div
      className="v3-head"
      data-od-id="page-head"
    >
      <h1>{t('admin_home_title', 'Admin')}</h1>
      <p>
        {t(
          'admin_home_subtitle',
          'Werkzeuge für interne Betreuung. Nur für Administratoren sichtbar.',
        )}
      </p>
    </div>
  );

  if (!data.isAuthenticated || !data.role) {
    return (
      <div className="v3-stack">
        {head}
        <V3EmptyBlock
          icon={V3UsersIcon}
          title={t('admin_home_empty_title', 'Kein Zugriff auf Admin-Daten')}
          text={t(
            'admin_home_empty_text',
            'Ihr Konto hat keine Admin-Berechtigung. Bitte wenden Sie sich an das Team.',
          )}
        />
      </div>
    );
  }

  const shortcut = (props: {
    readonly id: string;
    readonly to: string;
    readonly icon: ReactNode;
    readonly title: string;
    readonly text: string;
    readonly count?: string;
  }) => (
    <AppLink
      to={props.to}
      params={{ lang, companyId }}
      className="v3-card v3-shortcut"
      data-od-id={`shortcut-${props.id}`}
    >
      <span className="v3-row-between">
        <span
          className="v3-av4"
          aria-hidden="true"
        >
          {props.icon}
        </span>
        <span className="v3-btn ghost sm">
          {t('admin_home_open', 'Öffnen')} {V3ArrowIcon}
        </span>
      </span>
      <h2 className="text-base font-bold">{props.title}</h2>
      <p className="v3-mu v3-sm">{props.text}</p>
      {props.count ? <p className="v3-xs v3-mu v3-num">{props.count}</p> : null}
    </AppLink>
  );

  const accountRows: [string, string][] = [
    [
      t('admin_account_role', 'Rolle'),
      data.role,
    ],
    [
      t('admin_account_status', 'Angemeldet'),
      data.isAuthenticated ? t('common_yes', 'Ja') : t('common_no', 'Nein'),
    ],
    [
      t('admin_account_clerk_id', 'Clerk-ID'),
      data.clerkUserId || '–',
    ],
    [
      t('admin_account_hubspot_id', 'HubSpot-Kontakt'),
      data.hubspotContactId || '–',
    ],
  ];

  return (
    <div className="v3-stack">
      {head}

      <div className="v3-g2">
        {shortcut({
          id: 'evaluations',
          to: AppRoute.$LANG_$COMPANYID_ADMIN_EVALUATIONS,
          icon: V3DocIcon,
          title: t('admin_evaluations_title', 'Bewertungen'),
          text: t(
            'admin_evaluations_desc',
            'Alle Rechner-Bewertungen mit Status, Quelle und Links zu HubSpot.',
          ),
          count:
            data.evaluationsCount !== undefined
              ? `${data.evaluationsCount.toLocaleString('de-CH')} ${t(
                  'admin_evaluations_title',
                  'Bewertungen',
                )}`
              : undefined,
        })}
        {shortcut({
          id: 'users',
          to: AppRoute.$LANG_$COMPANYID_ADMIN_USERS,
          icon: V3UsersIcon,
          title: t('admin_users_title', 'Benutzer'),
          text: t(
            'admin_users_desc',
            'Clerk-Benutzer suchen, Rolle ändern, Aktivität prüfen.',
          ),
          count:
            data.usersCount !== undefined
              ? `${data.usersCount.toLocaleString('de-CH')} ${t(
                  'admin_users_title',
                  'Benutzer',
                )}`
              : undefined,
        })}
      </div>

      <section
        className="v3-card"
        data-od-id="account"
        aria-labelledby="h-ac"
      >
        <h2
          id="h-ac"
          className="text-base font-bold"
        >
          {t('admin_account_title', 'Ihr Konto')}
        </h2>
        <dl className="v3-kv">
          {accountRows.map(
            ([
              label,
              value,
            ]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd className="v3-num">{value}</dd>
              </div>
            ),
          )}
        </dl>
        <details>
          <summary
            className="v3-btn sm"
            style={{ width: 'fit-content' }}
          >
            {t('admin_tech_params', 'Technische Parameter')}
          </summary>
          <pre
            className="v3-pre v3-xs"
            style={{ marginTop: 8 }}
          >
            {JSON.stringify(data.params ?? {}, null, 2)}
          </pre>
        </details>
      </section>
    </div>
  );
}
